import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import {
    Accordion,
    AccordionDetails,
    AccordionProps,
    AccordionSummary,
    AccordionSummaryProps,
    Box,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { DeviceV3, TripDetails } from '../../../../backendsdk';
import { TrackedIconButton as IconButton } from '../../../../components/TrackedComponents';
import { SetAlert } from '../../../../hooks/alert';
import useApi from '../../../../hooks/api';
import useProfile from '../../../../hooks/profile';
import { formatLicensePlateWithInternalId } from '../../../../utils/Str';
import palette from '../../../ColorPalette';
import { RTLDirectionContext } from '../../../Layout';
import { parseIceServersFromCommandPayload } from '../LiveStream/Defs';
import GeneralDetailsPanel from './GeneralDetailsPanel';
import TripsPanel from './TripsPanel';
import WellnessPanel from './WellnessPanel';

export const MuiAccordion = styled((props: AccordionProps) => (
    <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
    backgroundColor: theme.palette.bgColor.main,
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&:before': {
        display: 'none',
    },
}));

export const MuiAccordionSummary = styled((props: AccordionSummaryProps) => <AccordionSummary {...props} />)(
    ({ theme }) => ({
        color: theme.palette.grayColor.main,
        '& .MuiTypography-root': {
            fontWeight: 'bold',
        },
    }),
);

interface DeviceDetailsComponentProps {
    devices: DeviceV3[];
    device: DeviceV3;
    selectedTrip?: TripDetails;
    setSelectedTrip: CallableFunction;
    timespan: 'day' | 'week';
    setTimespan: CallableFunction;
    additionalTrip?: TripDetails;
    isAdmin?: boolean;
    setAlert: SetAlert;
    setLiveStreamSessionId: (sessionId: number, vin?: string, iceServers?: RTCIceServer[]) => void;
}

const DeviceDetailsComponent: React.FC<DeviceDetailsComponentProps> = (props: DeviceDetailsComponentProps) => {
    const { device } = props;
    const { t } = useTranslation();
    const { profile } = useProfile();
    const history = useHistory();
    const isRTL = useContext(RTLDirectionContext);
    const { api } = useApi();
    const licensePlateLabel = formatLicensePlateWithInternalId(
        device.device.license_plate,
        device.device.internal_id,
        profile.customer.settings.show_vehicle_internal_id ?? false,
    );

    const currentDeviceIndex = props.devices.findIndex((d) => d.device.device_id === device.device.device_id);

    const handleLiveStreamClick = () => {
        api.apiV0CommandCommandIdPost({
            commandId: 0,
            remoteCommand: {
                device_id: device.device.device_id,
                command: 'live_stream',
            },
        })
            .then((res) => {
                // Parse payload: session_id, expiration, browser_credentials for ICE servers
                let sessionId: number | undefined;
                let iceServers: RTCIceServer[] | undefined;
                try {
                    const payload = JSON.parse(res.data.data || '{}');
                    sessionId = payload.session_id;
                    iceServers = parseIceServersFromCommandPayload(payload);
                } catch (e) {
                    // If payload parsing fails, continue without sessionId
                    console.error('Failed to parse session_id from response:', e);
                }
                // Only set sessionId if we have a valid sessionId (modal will open automatically)
                if (sessionId !== undefined) {
                    props.setLiveStreamSessionId(sessionId, device.device.license_plate, iceServers);
                } else {
                    // Handle case where sessionId is missing
                    props.setAlert({
                        message: t('content.fleet.details.general_details.live_stream.error_generic'),
                        type: 'error',
                        duration: 6000,
                    });
                }
            })
            .catch((error) => {
                // Check if error is due to existing live stream session
                // Backend may return: { error }, { detail }, detail as array, or raw array of messages
                const data = error?.response?.data;
                let errorMessage = '';
                if (data != null) {
                    if (Array.isArray(data)) {
                        errorMessage = data[0];
                    } else {
                        errorMessage = data.error ?? (Array.isArray(data.detail) ? data.detail[0] : data.detail) ?? '';
                    }
                }
                const errorString =
                    (typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage)) ||
                    error?.message ||
                    '';
                const isExistingSessionError = errorString
                    .toLowerCase()
                    .includes('device already has a pending live stream command');
                if (isExistingSessionError) {
                    props.setAlert({
                        message: t('content.fleet.details.general_details.live_stream.error_existing_session'),
                        type: 'error',
                        duration: 6000,
                    });
                } else {
                    const errorText =
                        errorString || t('content.fleet.details.general_details.live_stream.error_generic');
                    props.setAlert({
                        message: t('content.fleet.details.general_details.live_stream.error', { error: errorText }),
                        type: 'error',
                        duration: 6000,
                    });
                }
            });
    };
    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: 60,
                    flexShrink: 0,
                    backgroundColor: palette.neutral[200],
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LocalShippingIcon sx={{ fontSize: 28, ml: 1.75, mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {licensePlateLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title={t('content.fleet.details.previous')}>
                        <Box>
                            <IconButton
                                id="btn-previous-device"
                                disabled={currentDeviceIndex === 0}
                                onClick={() => {
                                    const previousDevice = props.devices[currentDeviceIndex - 1];
                                    history.push(
                                        `/overview/${encodeURIComponent(previousDevice.device.license_plate)}`,
                                    );
                                }}
                            >
                                {isRTL ? <ArrowForwardIosIcon /> : <ArrowBackIosNewIcon />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                    <Tooltip title={t('content.fleet.details.next')}>
                        <Box>
                            <IconButton
                                id="btn-next-device"
                                disabled={currentDeviceIndex === props.devices.length - 1}
                                onClick={() => {
                                    const nextDevice = props.devices[currentDeviceIndex + 1];
                                    history.push(`/overview/${encodeURIComponent(nextDevice.device.license_plate)}`);
                                }}
                                sx={{ mr: 1 }}
                            >
                                {isRTL ? <ArrowBackIosNewIcon /> : <ArrowForwardIosIcon />}
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={{ width: '100%', height: '100%', overflowY: 'auto', minHeight: 0 }}>
                <MuiAccordion defaultExpanded disableGutters>
                    <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} id="general-details-panel-header">
                        <Typography>{t('content.fleet.details.general_details.header')}</Typography>
                    </MuiAccordionSummary>
                    <AccordionDetails sx={{ pr: 1.5 }}>
                        <GeneralDetailsPanel
                            device={device}
                            onLiveStreamClick={handleLiveStreamClick}
                            isAdmin={props.isAdmin}
                        />
                    </AccordionDetails>
                </MuiAccordion>
                <MuiAccordion defaultExpanded disableGutters>
                    <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} id="wellness-panel-header">
                        <Typography>{t('content.fleet.details.wellness.header')}</Typography>
                    </MuiAccordionSummary>
                    <AccordionDetails>
                        <WellnessPanel device={device} />
                    </AccordionDetails>
                </MuiAccordion>
                <MuiAccordion defaultExpanded disableGutters>
                    <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} id="trips-panel-header">
                        <Typography>{t('content.fleet.details.trips.header')}</Typography>
                    </MuiAccordionSummary>
                    <AccordionDetails>
                        <TripsPanel {...props} />
                    </AccordionDetails>
                </MuiAccordion>
            </Box>
        </Box>
    );
};

export default DeviceDetailsComponent;
